import React, { ReactElement, useCallback, useMemo } from "react";
import cx from "classnames";

// dls
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// types
import { CartItemWithUUID, ItemImagesOnCartXPVariants } from "@swiggy-private/cart-types";
import { CART_ITEMS_TEST_IDS } from "@swiggy-private/cart-test-ids";

// widgets
import { AriaHidden } from "@swiggy-private/core-ui";
import { FoodSymbolIconSvg } from "@cart-ui/widgets/food-symbol-icon";
import { DeleteIconFilled, OutOfStockIcon, OutOfStockIconv4 } from "@cart-ui/components/food-item/icons";

// utils
import { isNonEmptyString } from "@swiggy-private/checkout-core";

// constants
import { CART_ITEM_IMAGE_PLACEHOLDER, CART_ITEM_IMAGE_SIZE } from "@cart-ui/components/food-item/constants";

import styles from "../styles/FoodItem.scss";
import { AddButtonContainer } from "../styles/ItemQty";
import { ReadOnlyBox, ReadTextBoxContainer } from "../styles/foodItemsStyles";

interface FoodItemTypeOutOfStockProps {
    children: ReactElement;
    item: CartItemWithUUID;
    disableActions: boolean;
    removeItem: VoidFunction;
    variant?: ItemImagesOnCartXPVariants;
    onItemImageLoad: () => void;
    isGroupOrderFlow?: boolean;
    isHost?: boolean;
    isCurrentItemFromSameUser?: boolean;
}

const ACCESSIBLITY_TEXTS = {
    REMOVE_OSS: "Remove out of stock item",
};
const REMOVE = "Remove";

const ReadTextBox = React.memo(function ReadTextBox({
    message,
    className,
    variant,
}: {
    message: string;
    className?: string;
    variant?: ItemImagesOnCartXPVariants;
}) {
    if (variant !== ItemImagesOnCartXPVariants.DEFAULT) {
        return (
            <AddButtonContainer>
                <ReadTextBoxContainer className={className}>
                    <Text variant="V2.caption.neutral.bold" color="text_Low_Emphasis">
                        {message}
                    </Text>
                </ReadTextBoxContainer>
            </AddButtonContainer>
        );
    }

    return (
        <ReadOnlyBox className={className}>
            <Text variant="V2.caption.neutral.bold" color="text_Low_Emphasis">
                {message}
            </Text>
        </ReadOnlyBox>
    );
});

const Remove = React.memo(function Remove({
    onClick,
    variant,
}: {
    onClick: VoidFunction;
    variant?: ItemImagesOnCartXPVariants;
}) {
    const removeClassname = cx(styles.remove, styles.removeIconCartV2, {
        [styles.removeSmall]: variant === ItemImagesOnCartXPVariants.SMALL,
    });

    return (
        <div
            className={removeClassname}
            onClick={onClick}
            aria-label={ACCESSIBLITY_TEXTS.REMOVE_OSS}
            role="button"
            data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_OOS_ITEM_REMOVE_ICON}
        >
            <AriaHidden as="span" className={styles.removeIcon}>
                {variant === ItemImagesOnCartXPVariants.BIG && (
                    <Text variant="V2.body3.neutral.bold" color="text_Med_Emphasis" className={styles.removeBig}>
                        {REMOVE} <DeleteIconFilled />
                    </Text>
                )}
                {variant === ItemImagesOnCartXPVariants.SMALL && <OutOfStockIcon />}
                {(!variant || variant === ItemImagesOnCartXPVariants.DEFAULT) && <OutOfStockIconv4 />}
            </AriaHidden>
        </div>
    );
});

export const FoodItemTypeOutOfStock = React.memo(function FoodItemTypeOutOfStock(
    props: FoodItemTypeOutOfStockProps,
): ReactElement {
    const showRemoveCta = useMemo(() => {
        if (!props.isGroupOrderFlow) return !props.disableActions;
        return props.isHost || props.isCurrentItemFromSameUser;
    }, [props.disableActions, props.isCurrentItemFromSameUser, props.isGroupOrderFlow, props.isHost]);

    const renderSmallImage = useCallback(() => {
        const cloudinaryId = isNonEmptyString(props.item.cloudinaryImageId)
            ? props.item.cloudinaryImageId
            : CART_ITEM_IMAGE_PLACEHOLDER;
        return (
            <>
                <div className={cx(styles.imageWraper, styles.imageWraperSmall)}>
                    <Image
                        cloudinaryId={cloudinaryId}
                        cloudinaryWidth={CART_ITEM_IMAGE_SIZE.SMALL * 2}
                        height={`${CART_ITEM_IMAGE_SIZE.SMALL}px`}
                        width={`${CART_ITEM_IMAGE_SIZE.SMALL}px`}
                        className={cx(styles.itemImage, styles.itemImageOOS)}
                        onLoad={props.onItemImageLoad}
                        data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_IMAGE}
                    />
                    <FoodSymbolIconSvg
                        vegType={props.item.isVeg}
                        className={cx(styles.symbol, styles.symbolForSmallImage, styles.itemImageOOS)}
                    />
                </div>
                {props.children}
                <ReadTextBox message={"Unavailable"} className={styles.foodItemActions} variant={props.variant} />
                {!props.disableActions && <Remove onClick={props.removeItem} variant={props.variant} />}
            </>
        );
    }, [
        props.children,
        props.disableActions,
        props.item.cloudinaryImageId,
        props.item.isVeg,
        props.onItemImageLoad,
        props.removeItem,
        props.variant,
    ]);

    const renderBigImage = useCallback(() => {
        const hasImage = isNonEmptyString(props.item.cloudinaryImageId);
        const actionsClassname = cx({
            [styles.foodItemActionsBig]: hasImage,
            [styles.foodItemActionsBigWithoutImage]: !hasImage,
        });
        const imageWrapperClassname = cx(styles.imageWraper, styles.imageWraperBig, {
            [styles.imageWraperBigNoImage]: !hasImage,
        });

        return (
            <div className={styles.itemImageBigContainer}>
                <div className={styles.itemDetails}>
                    <FoodSymbolIconSvg vegType={props.item.isVeg} />
                    {props.children}
                    {showRemoveCta && <Remove onClick={props.removeItem} variant={props.variant} />}
                </div>
                <div className={imageWrapperClassname}>
                    {props.item.cloudinaryImageId && (
                        <Image
                            cloudinaryId={props.item.cloudinaryImageId}
                            cloudinaryWidth={CART_ITEM_IMAGE_SIZE.BIG.WIDTH * 2}
                            height={`${CART_ITEM_IMAGE_SIZE.BIG.HEIGHT}px`}
                            width={`${CART_ITEM_IMAGE_SIZE.BIG.WIDTH}px`}
                            className={cx(styles.itemImage, styles.itemImageBig, styles.itemImageOOS)}
                            onLoad={props.onItemImageLoad}
                            data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_IMAGE}
                        />
                    )}
                    <ReadTextBox message={"Unavailable"} className={actionsClassname} variant={props.variant} />
                </div>
            </div>
        );
    }, [
        props.children,
        props.item.cloudinaryImageId,
        props.item.isVeg,
        props.onItemImageLoad,
        props.removeItem,
        props.variant,
        showRemoveCta,
    ]);

    return (
        <>
            {props.variant === ItemImagesOnCartXPVariants.SMALL && renderSmallImage()}
            {props.variant === ItemImagesOnCartXPVariants.BIG && renderBigImage()}
            {props.variant === ItemImagesOnCartXPVariants.DEFAULT && (
                <>
                    {props.children}
                    <ReadTextBox message={"Unavailable"} />
                    {showRemoveCta && <Remove onClick={props.removeItem} />}
                </>
            )}
        </>
    );
});
